/**
 * Tela de verificação OTP — 6 dígitos por e-mail.
 * Layout idêntico ao LoginPage (split metálico).
 * Inspirado no Isomorphic otp-5.
 */
import { ClipboardEvent, FormEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { PiSpinnerBold, PiArrowLeftBold } from 'react-icons/pi';

const authApi = axios.create({
    withCredentials: true,
    headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
    },
});
const csrf = (window as any).__AUTH_PAGE__?.csrfToken ?? '';
if (csrf) authApi.defaults.headers.common['X-CSRF-TOKEN'] = csrf;

const CODE_LENGTH = 6;

type Props = {
    maskedEmail: string;
    ttlMinutes: number;
    onBack: () => void;
};

/* ─── Linha decorativa dourada ─────────────────────────────── */
function GoldDivider() {
    return (
        <div
            className="h-px w-24"
            style={{ background: 'linear-gradient(90deg, transparent 0%, #C9A84C 50%, transparent 100%)' }}
        />
    );
}

export default function OtpPage({ maskedEmail, ttlMinutes, onBack }: Props) {
    const [digits, setDigits]       = useState<string[]>(Array(CODE_LENGTH).fill(''));
    const [loading, setLoading]     = useState(false);
    const [resending, setResending] = useState(false);
    const [errorMsg, setErrorMsg]   = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [cooldown, setCooldown]   = useState(0);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    /* Foco automático no primeiro campo ao montar */
    useEffect(() => { inputRefs.current[0]?.focus(); }, []);

    /* Countdown de reenvio */
    useEffect(() => {
        if (cooldown <= 0) return;
        const id = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
        return () => clearInterval(id);
    }, [cooldown]);

    /* Digita em uma caixa */
    const handleInput = (index: number, value: string) => {
        const digit = value.replace(/\D/g, '').slice(-1);
        const next = [...digits];
        next[index] = digit;
        setDigits(next);
        setErrorMsg('');

        if (digit && index < CODE_LENGTH - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    /* Backspace volta para anterior se vazio */
    const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !digits[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
        if (e.key === 'ArrowLeft' && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
        if (e.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    /* Cola o código (ex: copiou "123456" direto do e-mail) */
    const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
        if (!pasted) return;
        const next = Array(CODE_LENGTH).fill('');
        pasted.split('').forEach((c, i) => { next[i] = c; });
        setDigits(next);
        setErrorMsg('');
        const lastFilled = Math.min(pasted.length, CODE_LENGTH - 1);
        inputRefs.current[lastFilled]?.focus();
    };

    /* Submete */
    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        const code = digits.join('');
        if (code.length < CODE_LENGTH) {
            setErrorMsg('Digite os 6 dígitos do código.');
            return;
        }
        setLoading(true);
        setErrorMsg('');
        try {
            const { data } = await authApi.post('/login/verify-otp', { code });
            window.location.href = data.redirect ?? '/';
        } catch (err: any) {
            setErrorMsg(err?.response?.data?.message ?? 'Código inválido. Tente novamente.');
            setDigits(Array(CODE_LENGTH).fill(''));
            setTimeout(() => inputRefs.current[0]?.focus(), 50);
        } finally {
            setLoading(false);
        }
    };

    /* Reenviar */
    const handleResend = async () => {
        if (cooldown > 0 || resending) return;
        setResending(true);
        setErrorMsg('');
        setSuccessMsg('');
        try {
            await authApi.post('/login/resend-otp');
            setSuccessMsg('Novo código enviado para o seu e-mail.');
            setCooldown(60);
            setDigits(Array(CODE_LENGTH).fill(''));
            setTimeout(() => inputRefs.current[0]?.focus(), 50);
        } catch (err: any) {
            setErrorMsg(err?.response?.data?.message ?? 'Erro ao reenviar. Tente novamente.');
        } finally {
            setResending(false);
        }
    };

    const allFilled = digits.every((d) => d !== '');

    return (
        <div className="flex min-h-screen">

            {/* ── Coluna esquerda: formulário ──────────────── */}
            <div className="flex w-full flex-col justify-between px-6 py-10 sm:px-10 lg:w-[48%] lg:px-16 xl:px-24">
                {/* Logo */}
                <div>
                    <img src="/img/design/Logo_Buckler_Oldblack.png" alt="Buckler Fit" className="h-8" />
                </div>

                {/* Formulário OTP */}
                <div className="mx-auto w-full max-w-md">
                    {/* Voltar */}
                    <button
                        type="button" onClick={onBack}
                        className="mb-6 flex items-center gap-1.5 text-sm font-medium text-[#9A7B28] hover:text-[#C9A84C] transition-colors"
                    >
                        <PiArrowLeftBold className="size-4" /> Voltar ao login
                    </button>

                    <h2 className="text-2xl font-bold text-gray-900 sm:text-3xl">Digite o código</h2>
                    <p className="mt-1.5 text-sm text-gray-500">
                        Enviamos um código de verificação para{' '}
                        <strong className="font-semibold text-gray-700">{maskedEmail}</strong>.
                        <br />
                        O código expira em{' '}
                        <span className="font-semibold text-[#9A7B28]">{ttlMinutes} minuto{ttlMinutes !== 1 ? 's' : ''}</span>.
                    </p>

                    <form onSubmit={handleSubmit} className="mt-8">
                        {/* 6 caixas individuais */}
                        <div className="flex justify-between gap-2 sm:gap-3">
                            {digits.map((digit, i) => (
                                <input
                                    key={i}
                                    ref={(el) => { inputRefs.current[i] = el; }}
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete={i === 0 ? 'one-time-code' : 'off'}
                                    maxLength={1}
                                    value={digit}
                                    onChange={(e) => handleInput(i, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(i, e)}
                                    onPaste={i === 0 ? handlePaste : undefined}
                                    onFocus={(e) => e.target.select()}
                                    className={`
                                        h-14 w-full rounded-xl border-2 text-center text-xl font-bold tabular-nums
                                        text-gray-900 outline-none transition-all
                                        ${digit
                                            ? 'border-[#C9A84C] bg-[#FDF5DC]'
                                            : 'border-gray-200 bg-white'
                                        }
                                        focus:border-[#C9A84C] focus:ring-2 focus:ring-[#C9A84C]/20
                                        ${errorMsg ? 'border-red-400 bg-red-50' : ''}
                                    `}
                                />
                            ))}
                        </div>

                        {/* Mensagens */}
                        {errorMsg && (
                            <p className="mt-3 text-center text-sm font-medium text-red-500">{errorMsg}</p>
                        )}
                        {successMsg && (
                            <p className="mt-3 text-center text-sm font-medium text-emerald-600">{successMsg}</p>
                        )}

                        {/* Botão verificar */}
                        <button
                            type="submit"
                            disabled={loading || !allFilled}
                            className="btn-gold mt-6 flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm"
                        >
                            {loading && <PiSpinnerBold className="size-4 animate-spin" />}
                            {loading ? 'Verificando…' : 'Verificar código'}
                        </button>

                        {/* Reenviar */}
                        <div className="mt-5 text-center">
                            <p className="text-sm text-gray-500">
                                Não recebeu?{' '}
                                <button
                                    type="button"
                                    onClick={handleResend}
                                    disabled={cooldown > 0 || resending}
                                    className="font-semibold text-[#9A7B28] hover:text-[#C9A84C] disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
                                >
                                    {resending
                                        ? 'Enviando…'
                                        : cooldown > 0
                                        ? `Reenviar em ${cooldown}s`
                                        : 'Reenviar código'}
                                </button>
                            </p>
                        </div>
                    </form>
                </div>

                {/* Rodapé */}
                <p className="text-center text-xs text-gray-400">
                    © {new Date().getFullYear()} Buckler Fit · Gestão de Importações
                </p>
            </div>

            {/* ── Coluna direita: painel metálico escuro (idêntico ao LoginPage) ── */}
            <div
                className="relative hidden flex-col items-center justify-center overflow-hidden lg:flex lg:w-[52%]"
                style={{ background: 'linear-gradient(160deg, #0f0d08 0%, #1a1508 40%, #0f0d08 100%)' }}
            >
                <div className="pointer-events-none absolute inset-0">
                    <div
                        className="absolute left-1/2 top-1/2 size-[700px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-20 blur-3xl"
                        style={{ background: 'radial-gradient(circle, #C9A84C 0%, transparent 70%)' }}
                    />
                    <div className="absolute -left-24 bottom-0 size-72 rounded-full opacity-10 blur-2xl" style={{ background: '#D4AF37' }} />
                    <div className="absolute -right-24 top-0 size-72 rounded-full opacity-10 blur-2xl" style={{ background: '#9A7B28' }} />
                </div>
                <div className="absolute left-0 top-0 h-full w-px"
                    style={{ background: 'linear-gradient(180deg, transparent 0%, #C9A84C 30%, #E8CC6A 50%, #C9A84C 70%, transparent 100%)' }} />

                <div className="relative z-10 flex flex-col items-center px-12 text-center">
                    <img src="/img/design/Logo_Buckler_OffWhite.png" alt="Buckler Fit" className="mb-10 h-12 opacity-90" />
                    <GoldDivider />
                    <blockquote className="mt-8 max-w-sm">
                        <p className="text-2xl font-bold leading-snug" style={{ color: '#E8CC6A' }}>
                            "Queira mais que os outros."
                        </p>
                        <footer className="mt-6">
                            <p className="text-sm font-bold text-white/80">Bruno Parlato</p>
                            <p className="mt-1 text-xs italic text-white/35">
                                "Deve ser ruim p*ra car*lho não ser Buckler."
                            </p>
                        </footer>
                    </blockquote>
                    <GoldDivider />
                </div>
            </div>
        </div>
    );
}
